import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useId, useState } from "react"
import { useNavigate } from "react-router"
import { toast } from "sonner"
import { SettingsPanel, SettingsRow, SettingsSection } from "@/design-system/index.ts"
import { useInstallOffer } from "@/installed-app/install-offer.tsx"
import { Button } from "@/primitives/button.tsx"
import { Switch } from "@/primitives/switch.tsx"
import {
  pushDeviceKey,
  pushKeyQuery,
  readPushState,
  setPushPreviews,
  turnOffPush,
  turnOnPush,
  type PushState,
} from "./push-device.ts"
import { pathOf, pushOpenMessage } from "./push-notification.ts"

/*
 * Chat push on this browser, as the Member sees and changes it: the "On this device" section of
 * Chat notifications, and the shell's `ChatPushHost`, which keeps this browser current on each
 * visit and opens what a notification points at in the tab already open.
 */

/** Where push stands here (`PushState`), keeping this browser current while it is subscribed. */
function usePushState() {
  const { route } = useInstallOffer()
  const key = useQuery(pushKeyQuery)
  // A web API without push routes answers none: push isn't set up there.
  const publicKey = key.data?.publicKey ?? null
  return useQuery({
    queryKey: [...pushDeviceKey, route, publicKey],
    queryFn: () => readPushState(route, publicKey),
    enabled: !key.isPending,
    staleTime: 5 * 60_000,
    retry: false,
  })
}

/** What each state says under the switch, and whether the switch can change anything. */
const helpers: Record<PushState["state"], string> = {
  unsupported: "This browser can't show Beacon's notifications.",
  "install-first":
    "On an iPhone or iPad, Beacon can notify you once you add it to your Home Screen. Open it from there and turn notifications on.",
  "not-set-up": "Notifications aren't set up on this Beacon yet.",
  blocked:
    "Notifications are blocked for Beacon in this browser. Allow them in its site settings, then come back here.",
  off: "Your browser asks first. A notification says only that something is for you, unless you turn on previews.",
  on: "A notification says only that something is for you, unless you turn on previews.",
}

/** "On this device": turning this browser's notifications, and their previews, on or off. */
export function PushSection() {
  const queryClient = useQueryClient()
  const key = useQuery(pushKeyQuery)
  const push = usePushState()
  const { install } = useInstallOffer()
  const [busy, setBusy] = useState(false)
  const notifySwitch = useId()
  const previewSwitch = useId()
  const state = push.data
  const on = state?.state === "on"

  async function run(work: () => Promise<unknown>, failed: string) {
    setBusy(true)
    try {
      await work()
    } catch {
      toast.error(failed)
    } finally {
      await queryClient.invalidateQueries({ queryKey: pushDeviceKey })
      setBusy(false)
    }
  }

  async function turn(next: boolean) {
    const publicKey = key.data?.publicKey
    if (!next) return run(turnOffPush, "Couldn't turn notifications off here. Try again.")
    if (!publicKey) return
    await run(async () => {
      const permission = await turnOnPush(publicKey)
      if (permission === "denied") toast.error("Notifications are blocked for Beacon here.")
    }, "Couldn't turn notifications on here. Try again.")
  }

  return (
    <SettingsSection
      id="this-device"
      title="On this device"
      description="Notifications from Chat in this browser, as your choices above and Do not disturb allow. Each device you turn them on for is its own."
    >
      <SettingsPanel>
        <SettingsRow
          layout="inline"
          control="switch"
          label="Notifications"
          htmlFor={notifySwitch}
          helper={
            <span role="status">
              {state ? helpers[state.state] : "Checking what this browser can do…"}
            </span>
          }
        >
          <Switch
            id={notifySwitch}
            checked={on}
            disabled={busy || !state || (state.state !== "on" && state.state !== "off")}
            onCheckedChange={(next) => void turn(next)}
          />
        </SettingsRow>
        {state?.state === "install-first" && (
          <SettingsRow layout="inline" control="button" label="Add Beacon to your Home Screen">
            <Button variant="outline" size="sm" onClick={install}>
              Install Beacon
            </Button>
          </SettingsRow>
        )}
        {on && (
          <SettingsRow
            layout="inline"
            control="switch"
            label="Show previews"
            htmlFor={previewSwitch}
            helper="Show who sent each message, where, and how it starts. Anyone who can see this screen can read them."
          >
            <Switch
              id={previewSwitch}
              checked={state.previews}
              disabled={busy}
              onCheckedChange={(previews) =>
                void run(
                  () => setPushPreviews(previews),
                  "Couldn't change previews here. Try again.",
                )
              }
            />
          </SettingsRow>
        )}
      </SettingsPanel>
    </SettingsSection>
  )
}

/**
 * The shell's part in Chat push: keeps this browser current on each visit, for the session now
 * signed in here when its Member is the one who turned push on, and opens what a notification
 * points at when the service worker hands it to this tab rather than opening another.
 */
export function ChatPushHost() {
  usePushState()
  const navigate = useNavigate()
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return
    const workers = navigator.serviceWorker
    const opened = (event: MessageEvent) => {
      const said = event.data as { type?: unknown; path?: unknown } | null
      if (said?.type === pushOpenMessage) void navigate(pathOf({ path: said.path }))
    }
    workers.addEventListener("message", opened)
    return () => workers.removeEventListener("message", opened)
  }, [navigate])
  return null
}
